import { useCallback, useEffect, useState } from 'react'

import type { HistoryWindow } from '@/components/charts/history/useHistoryNavigation'
import { useApi } from '@/hooks'
import { buildQuery } from '@/insights/common/queryUtils'
import { SAMPLE_LIMIT } from '@/insights/fuel/reeferFuelGraph/constants'
import { toWallClock } from '@/insights/fuel/reeferFuelGraph/window'

export interface AssetSamples<T> {
  /** The first window's samples; later windows are the chart's own (see `reload`). */
  data: T[] | undefined
  error: string
  isLoading: boolean
  /** No samples in the window last loaded, first or later. */
  isEmpty: boolean
  reload: (api: unknown, window: HistoryWindow) => Promise<T[] | undefined>
}

/**
 * One asset's samples from a per-asset insight endpoint (`/insights/fuel/reefer-fuel-by-asset`,
 * `/insights/speed/reefer-speed-by-asset`), for the asset detail Graphs tab.
 *
 * These endpoints take an explicit `to` and both bounds as zone-less wall clock in the user's
 * profile timezone, not the offset-bearing `from` + `periodHours` of `/assets/history`: an ISO
 * string with an offset here would shift the window by the user's UTC offset and quietly
 * return the wrong data (see FuelGraphPanel).
 */
export const useAssetSamples = <T>(
  path: string,
  companyId: number,
  assetId: number,
  /** Must be stable (e.g. `useAssetWindow`'s `initial`): a new object refetches. */
  firstWindow: HistoryWindow
): AssetSamples<T> => {
  const { api } = useApi()
  const [data, setData] = useState<T[]>()
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [isEmpty, setIsEmpty] = useState(false)

  const fetchWindow = useCallback(
    async (window: HistoryWindow): Promise<T[]> => {
      const query = buildQuery({
        companyId,
        assetIds: String(assetId),
        date: 'custom',
        from: toWallClock(window.from),
        to: toWallClock(window.to),
      })
      const response = await api.get<T[]>({
        path: `${path}?${query}&skip=0&take=${SAMPLE_LIMIT}`,
        origin: 'useAssetSamples#fetchWindow',
      })
      if (response.error) {
        throw new Error(response.message || 'Unable to load graph data.')
      }
      const rows = response.data ?? []
      setIsEmpty(rows.length === 0)
      return rows
    },
    [api, path, companyId, assetId]
  )

  const reload = useCallback(
    // IMPORTANT: don't push the result back into `data` — the chart owns its window
    // internally, and changing the `data` prop would make it treat the fetch as a
    // reconfigure and discard the user's range.
    async (_: unknown, window: HistoryWindow): Promise<T[] | undefined> => {
      try {
        return await fetchWindow(window)
      } catch {
        return undefined
      }
    },
    [fetchWindow]
  )

  useEffect(() => {
    let isActive = true
    setIsLoading(true)
    setError('')
    fetchWindow(firstWindow)
      .then((rows) => {
        if (isActive) setData(rows)
      })
      .catch(() => {
        if (isActive) {
          setData(undefined)
          setError('Unable to load graph data.')
        }
      })
      .finally(() => {
        if (isActive) setIsLoading(false)
      })
    return () => {
      isActive = false
    }
  }, [fetchWindow, firstWindow])

  return { data, error, isLoading, isEmpty, reload }
}
